import { useCallback, useMemo } from 'react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import { ActivityCatalog } from 'modules/core/lib/activities/catalog'
import { useActivities } from 'modules/core/lib/activities/provider'

import { copyInboxTaskLink } from 'modules/forms/lib/shareLink'

const ACTIVITY_IDS = {
  publishSurvey: 'engagement_surveys.publishSurvey' as const,
  finishSurvey: 'engagement_surveys.finishSurvey' as const,
  duplicateSurvey: 'engagement_surveys.duplicateSurvey' as const,
  archiveSurvey: 'engagement_surveys.archiveSurvey' as const,
  deleteSurvey: 'engagement_surveys.deleteSurvey' as const,
  remindRespondents: 'engagement_surveys.remindRespondents' as const,
  unscheduleSurvey: 'engagement_surveys.unscheduleSurvey' as const,
} satisfies Record<string, ActivityCatalog>

type UseSurveyActionsParams = {
  formId: string
}

export function useSurveyActions({ formId }: UseSurveyActionsParams) {
  const { surveyId } = useStrictParams<'surveyId'>()
  const { openActivity } = useActivities()
  const { navigation } = useNavigation()

  type ActionData = {
    surveyId?: string
    formId?: string
    membershipId?: string
    resourceType?: string
    mode?: 'preview' | 'answer'
    renderingMode?: 'stepped' | 'all-questions'
  }

  const actionKeyToActivity: Record<
    string,
    { activity: ActivityCatalog; data: ActionData } | undefined
  > = useMemo(
    () => ({
      publish: {
        activity: ACTIVITY_IDS.publishSurvey,
        data: { surveyId: surveyId.toString() },
      },
      'finish-survey': {
        activity: ACTIVITY_IDS.finishSurvey,
        data: { formId },
      },
      duplicate: {
        activity: ACTIVITY_IDS.duplicateSurvey,
        data: { surveyId: surveyId.toString() },
      },
      archive: {
        activity: ACTIVITY_IDS.archiveSurvey,
        data: { surveyId: surveyId.toString() },
      },
      delete: {
        activity: ACTIVITY_IDS.deleteSurvey,
        data: { surveyId: surveyId.toString() },
      },
      'remind-respondents': {
        activity: ACTIVITY_IDS.remindRespondents,
        data: { surveyId: surveyId.toString(), formId },
      },
      unschedule: {
        activity: ACTIVITY_IDS.unscheduleSurvey,
        data: { surveyId: surveyId.toString() },
      },
    }),
    [surveyId, formId]
  )

  const handleActionClick = useCallback(
    (key: string) => {
      if (key === 'share-link') {
        const inboxPath = navigation.inbox.todo.fullPath()
        copyInboxTaskLink(inboxPath, 'Forms::Form', formId)
        return
      }

      const activityConfig = actionKeyToActivity[key]
      if (activityConfig) {
        openActivity(activityConfig.activity, activityConfig.data)
      }
    },
    [openActivity, actionKeyToActivity, navigation, formId]
  )

  return {
    handleActionClick,
  }
}
